import type React from "react";
import type {ContractDetail, ContractDocument, ContractVersion} from "../contractExplorerTypes.ts";
import {LIFECYCLE_NOTE} from "../contractExplorerTypes.ts";
import {baseDocument, endBasisLabel, formatDate, SIGNATURE_LABEL, termOf, typeLabel} from "../contractExplorerFormat.ts";
import {FactValue} from "./FactValue.tsx";
import {KeyValue} from "./KeyValue.tsx";
import {Section} from "./Section.tsx";
import {SourceCite} from "./SourceCite.tsx";

type Props = {
    contract: ContractDetail;
    version: ContractVersion | undefined;
    documents: Record<string, ContractDocument>;
};

/** The bundle's metadata: what it is, with whom, and the period the shown version covers. */
export const BundleFacts: React.FC<Props> = ({contract, version, documents}) => {
    const base = baseDocument(contract);
    const cite = <SourceCite documentId={base?.document_id} documents={documents}/>;
    const signature = termOf(contract, version, 'signature_status');
    const signedOn = termOf(contract, version, 'signed_date');
    return (
        <Section title="Bundle metadata" sub={contract.summary || undefined}>
            <div className="ci-card ci-kv">
                <KeyValue label="Bundle title">{contract.title}{cite}</KeyValue>
                <KeyValue label="Supplier"
                          note={contract.linkage.supplier_label
                              ? `Matched to ${contract.linkage.supplier_label} in the spend data`
                              : 'Not matched to a supplier in the spend data'}>
                    {contract.supplier_name || <span className="ci-dash">—</span>}
                </KeyValue>
                <KeyValue label="Document type" note={contract.contract_type ? `Subject: ${typeLabel(contract.contract_type).toLowerCase()}` : undefined}>
                    {typeLabel(contract.document_type)}{cite}
                </KeyValue>
                <KeyValue label="Buying entities"
                          note={contract.linkage.entities.length
                              ? `Matched to ${contract.linkage.entities.join(', ')} in the spend data`
                              : 'Not matched to an entity in the spend data'}>
                    {contract.buying_entities.length ? contract.buying_entities.join(' · ') : <span className="ci-dash">—</span>}
                </KeyValue>
                <KeyValue label="Stage" note={LIFECYCLE_NOTE[contract.lifecycle]}>
                    {contract.stage === 'draft' ? 'Draft: no signature found' : 'Signed'}{cite}
                </KeyValue>
                <KeyValue label="Signed" note={signature ? SIGNATURE_LABEL[String(signature.value)] : undefined}>
                    {signedOn ? <>{formatDate(String(signedOn.value))}
                        <SourceCite documentId={signedOn.documentId} documents={documents}/></> : <span className="ci-dash">—</span>}
                </KeyValue>
                <KeyValue label="Reference">
                    <FactValue fact={termOf(contract, version, 'contract_reference')} documents={documents}/>
                </KeyValue>
                <KeyValue label="Version starts" note={version?.from_basis ? `From ${typeLabel(version.from_basis).toLowerCase()}` : 'No document states a start'}>
                    {formatDate(version?.valid_from)}
                    <SourceCite documentId={version?.from_document} documents={documents}/>
                </KeyValue>
                <KeyValue label="Version ends" note={version ? endBasisLabel(version.to_basis) : undefined}>
                    {version?.valid_to ? formatDate(version.valid_to) : 'Open'}
                </KeyValue>
                <KeyValue label="Bundle period" note={endBasisLabel(contract.end_basis)}>
                    {formatDate(contract.starts_on)} → {contract.ends_on ? formatDate(contract.ends_on) : 'no end date'}
                </KeyValue>
                <KeyValue label="Line currency">{contract.currency || <span className="ci-dash">—</span>}</KeyValue>
                <KeyValue label="Scope"><FactValue fact={termOf(contract, version, 'scope')} documents={documents}/></KeyValue>
                <KeyValue label="Site"><FactValue fact={termOf(contract, version, 'site_address')} documents={documents}/></KeyValue>
            </div>
        </Section>
    );
};
